import React, { forwardRef, useImperativeHandle, useRef, cloneElement, isValidElement, useState, useEffect } from "react";
import { Box, View } from "components/ui";
import { SiteSelect } from "components/form/SiteSelect";

export type SiteSelectButtonProps = Omit<React.ComponentProps<typeof SiteSelect>, "onSelect"> & {
	children: ((props: { onPress: () => void }) => React.ReactNode) | React.ReactNode;
	onSelect?: (selectedItem: any, index: number) => void;
	value?: any;
};

export const SiteSelectButton = forwardRef<any, SiteSelectButtonProps>(({ children, onSelect, value, ...props }, ref) => {
	const siteSelectRef = useRef<any>(null);
	const dropdownButtonRef = useRef(null);
	const [internalValue, setInternalValue] = useState(value);

	// Update internal value when external value changes
	useEffect(() => {
		setInternalValue(value);
	}, [value]);

	useImperativeHandle(ref, () => ({
		open: () => siteSelectRef.current?.open(),
		close: () => siteSelectRef.current?.close(),
		getValue: () => internalValue,
	}));

	const handleOpen = () => siteSelectRef.current?.open();

	// Handle selection internally when no external handler is provided
	const handleSelect = (selectedItem: any, index: number) => {
		// Always update our internal state
		setInternalValue(selectedItem);

		// If external handler exists, call it
		if (onSelect) {
			onSelect(selectedItem, index);
		}
	};

	const renderChildren = () => {
		if (typeof children === "function") {
			return children({
				onPress: handleOpen,
			});
		} else if (isValidElement(children)) {
			return cloneElement(children, {
				onPress: handleOpen,
				...(children.props.onPress
					? {
							onPress: (...args: any[]) => {
								handleOpen();
								children.props.onPress(...args);
							},
						}
					: {}),
				...children.props,
			});
		}
		return children;
	};

	return (
		<>
			<View ref={dropdownButtonRef}>{renderChildren()}</View>

			<Box display="none">
				<SiteSelect ref={siteSelectRef} dropdownButtonRef={dropdownButtonRef} onSelect={handleSelect} value={internalValue || value} {...props} />
			</Box>
		</>
	);
});
